import React, {
  FC,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

import {
  Tab as ReactTab,
  Tabs as ReactTabs,
  TabList as ReactTabList,
  TabListProps as ReactTabListProps,
  TabPanel as ReactTabPanel,
  TabsProps as ReactTabsProps,
  TabProps as ReactTabProps,
} from "react-tabs"
import styled, { css, FlattenSimpleInterpolation } from "styled-components"
import { TestableComponent } from "../../e2e-tests/utils/locator-utils"

import { assertNever } from "../../typings/typeUtils"
import { theme } from "../../util/styles/theme"
import { Icon, IconProps } from "../icon/Icon"

export const SELECTED_TAB_CLASSNAME = "selected-tab"

interface TabProps {
  value: React.ReactNode
}

type TabPanelProps = {
  content: React.ReactNode
}

type TabListProps = {
  alignment?: "left" | "center"
  padding?: boolean
} & Pick<Required<TabsProps>, "variant"> &
  Pick<ReactTabListProps, "ref">

type StyledTabProps = ReactTabProps & {
  index: number
  tabs: number
  active: number
  tabsize: "medium" | "large"
} & Pick<Required<TabsProps>, "variant">

type StyledTabPanelProps = { visible?: boolean } & Pick<
  Required<TabsProps>,
  "variant"
>

interface TabsProps extends ReactTabsProps {
  alignment?: "left" | "center"
  padding?: boolean
  componentSize?: "medium" | "large"
  tabs: TabProps[]
  tabPanels: TabPanelProps[]
  variant?: "pills" | "contained"
  className?: string
  hideTabList?: boolean
}

const getMultipliedSpacing = (factor: number): string => {
  return `calc(${theme.spacings.medium} * ${factor})`
}

/*
  Active tab is the most upfront, with the largest z-index
  Tab furthest away from the active tab has the smallest z-index
  */
const getTabStyle = (
  index: number,
  tabs: number,
  active: number,
): FlattenSimpleInterpolation => {
  if (index < active) {
    const zIndex = tabs - (active - index)
    return css`
      z-index: ${zIndex};
      margin-right: ${getMultipliedSpacing(-3)};
      padding-left: ${getMultipliedSpacing(2)};
      padding-right: ${getMultipliedSpacing(4)};
    `
  }
  if (index > active) {
    const zIndex = tabs - (index - active)
    return css`
      z-index: ${zIndex};
      margin-left: ${getMultipliedSpacing(-3)};
      padding-left: ${getMultipliedSpacing(4)};
      padding-right: ${getMultipliedSpacing(2)};
    `
  }
  const zIndex = tabs
  return css`
    z-index: ${zIndex};
    padding-left: ${getMultipliedSpacing(2)};
    padding-right: ${getMultipliedSpacing(2)};
  `
}

const TabList = styled(ReactTabList)<TabListProps>`
  display: flex;
  flex-direction: row;
  justify-content: ${({ alignment }) => alignment};
  margin: 0 0 ${theme.spacings.xlarge};

  ${({ variant, padding }) => {
    switch (variant) {
      case "pills":
        return css`
          padding-left: ${padding ? theme.spacings.medium : 0};
        `
      case "contained":
        return css`
          padding: 0;
          margin: 0;
        `
      default:
        return assertNever(variant)
    }
  }}
`

const TabPanel = styled(ReactTabPanel)<StyledTabPanelProps>`
  display: ${({ visible }) => (visible ? "block" : "none")};

  ${({ variant }) => {
    switch (variant) {
      case "pills":
        return null
      case "contained":
        return css`
          margin: ${theme.spacings.medium};
        `
      default:
        return assertNever(variant)
    }
  }}
`

const Tab = styled(ReactTab)<StyledTabProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  background: ${theme.colors.white};
  color: ${theme.colors.blue};

  &.${SELECTED_TAB_CLASSNAME} {
    background: ${theme.colors.orange};
    color: ${theme.colors.white};

    &:focus-visible {
      // TODO Disable focus ring which sometimes appears in at least chrome. This might be bad for accessibility, so
      // should maybe define nice looking focus ring
      outline: none;
    }
  }

  &:not(.${SELECTED_TAB_CLASSNAME}):hover {
    background: ${theme.colors.greyCold};
    border-color: ${theme.colors.greyCold};
  }
  cursor: pointer;

  ${({ tabsize }) => {
    switch (tabsize) {
      case "medium":
        return css`
          padding-top: ${theme.spacings.small};
          padding-bottom: ${theme.spacings.small};
          ${theme.textStyle("default", "medium", "medium")}
        `
      case "large":
        return css`
          padding-top: ${theme.spacings.small};
          padding-bottom: ${theme.spacings.small};
          ${theme.textStyle("default", "medium", "medium")}

          ${theme.breakpoints.sm} {
            ${theme.textStyle("default", "large", "medium")}
            padding-top: ${theme.spacings.medium};
            padding-bottom: ${theme.spacings.medium};
          }
        `
      default:
        return assertNever(tabsize)
    }
  }}

  ${({ variant, index, tabs, active }) => {
    switch (variant) {
      case "pills":
        return css`
          border-radius: ${theme.borderRadius.medium};
          ${getTabStyle(index, tabs, active)}
        `
      case "contained":
        return css`
          padding: ${theme.spacings.small} ${theme.spacings.medium};
          &:hover:after {
            content: " ";
            display: block;
            position: absolute;
            left: 0;
            right: 0;
            height: 2px;
            bottom: 0;
            background: ${theme.colors.orange};
          }
        `
      default:
        return assertNever(variant)
    }
  }}
`

type IconTabProps = Pick<IconProps, "icon"> & {
  label: string
}

const TabIcon = styled(Icon)`
  ${theme.iconSizing()}
  flex-shrink: 0;

  margin-right: ${theme.spacings.small};

  ${Tab}.${SELECTED_TAB_CLASSNAME} & {
    fill: ${theme.colors.white};
    color: ${theme.colors.white};
  }
`
const TabLabel = styled.span``

export const IconTab: React.FC<IconTabProps> = ({ label, icon }) => {
  return (
    <>
      <TabIcon icon={icon} color="orange" />
      <TabLabel>{label}</TabLabel>
    </>
  )
}

type TabListWrapperProps = {
  showScrollIndicators: boolean
  rightScrollIndicatorVisible: boolean
  leftScrollIndicatorVisible: boolean
} & Pick<TabsProps, "hideTabList">

const TabListWrapper = styled.div<TabListWrapperProps>`
  position: relative;

  ${({ showScrollIndicators }) =>
    showScrollIndicators &&
    css`
      &:before,
      &:after {
        bottom: 0;
        content: "";
        pointer-events: none;
        position: absolute;
        top: 0;
        width: 5rem;
        z-index: 100;
        display: block;
        opacity: 0;
        transition: opacity 0.3s;
      }

      &:before {
        left: 0;
        background: linear-gradient(
          to left,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0.75) 50%,
          rgba(255, 255, 255, 1) 100%
        );
      }

      &:after {
        right: 0;
        background: linear-gradient(
          to right,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0.75) 50%,
          rgba(255, 255, 255, 1) 100%
        );
      }
    `}


  ${({ rightScrollIndicatorVisible }) =>
    rightScrollIndicatorVisible &&
    css`
      &:after {
        opacity: 1;
      }
    `}

  ${({ leftScrollIndicatorVisible }) =>
    leftScrollIndicatorVisible &&
    css`
      &:before {
        opacity: 1;
      }
    `}

  display: ${({ hideTabList }) => (hideTabList ? "none" : undefined)};
`

const TabListContainer = styled.div<Pick<TabListProps, "variant">>`
  display: block;
  overflow-x: auto;
  scroll-behavior: smooth;
  ${({ variant }) => {
    switch (variant) {
      case "pills":
        return null
      case "contained":
        return css`
          border-bottom: 1px solid ${theme.colors.greyDark};
        `
      default:
        return assertNever(variant)
    }
  }}
`

// Defines the tolerance when scroll fade indicator is shown
const SCROLL_FADE_TOLERANCE = 20 /* pixels */

export const Tabs: FC<React.PropsWithChildren<TabsProps>> &
  TestableComponent = ({
  alignment,
  padding,
  tabs,
  tabPanels,
  componentSize,
  variant = "pills",
  className,
  hideTabList,
  forceRenderTabPanel,
  ...rest
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [showLeftScrollIndicator, setShowLeftScrollIndicator] = useState(false)
  const [showRightScrollIndicator, setShowRightScrollIndicator] = useState(
    false,
  )

  const showScrollIndicators = variant === "contained"

  const tabListRef = useRef<HTMLDivElement>(null)

  const refreshTabListScrollIndicators = useCallback(() => {
    if (tabListRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabListRef.current
      setShowLeftScrollIndicator(scrollLeft > SCROLL_FADE_TOLERANCE)
      setShowRightScrollIndicator(
        scrollLeft + clientWidth + SCROLL_FADE_TOLERANCE <= scrollWidth,
      )
    } else {
      setShowLeftScrollIndicator(false)
      setShowRightScrollIndicator(false)
    }
  }, [])

  const handleTabListScroll = useCallback(() => {
    refreshTabListScrollIndicators()
  }, [refreshTabListScrollIndicators])

  useEffect(() => {
    const tabListElement = tabListRef.current

    if (!showScrollIndicators || !tabListElement) {
      return undefined
    }

    tabListElement.addEventListener("scroll", handleTabListScroll)

    return () =>
      tabListElement.removeEventListener("scroll", handleTabListScroll)
  }, [
    handleTabListScroll,
    refreshTabListScrollIndicators,
    showScrollIndicators,
  ])

  useEffect(() => {
    if (!showScrollIndicators) {
      return undefined
    }

    refreshTabListScrollIndicators()

    window.addEventListener("resize", refreshTabListScrollIndicators)
    return () =>
      window.removeEventListener("resize", refreshTabListScrollIndicators)
  }, [refreshTabListScrollIndicators, showScrollIndicators])

  return (
    <ReactTabs
      onSelect={(index: number) => setSelectedIndex(index)}
      selectedIndex={selectedIndex}
      selectedTabClassName={SELECTED_TAB_CLASSNAME}
      className={[Tabs.testClassName ?? "", className ?? ""]}
      forceRenderTabPanel={forceRenderTabPanel}
      {...rest}
    >
      <TabListWrapper
        leftScrollIndicatorVisible={showLeftScrollIndicator}
        rightScrollIndicatorVisible={showRightScrollIndicator}
        showScrollIndicators={showScrollIndicators}
        hideTabList={hideTabList}
      >
        <TabListContainer ref={tabListRef} variant={variant}>
          <TabList alignment={alignment} padding={padding} variant={variant}>
            {tabs.map((tab, index: number) => {
              return (
                <Tab
                  // There is no other natural key for tabs and tabpanels, so knowingly
                  // using index here. Order of tabs remains the same in our use cases.
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  index={index}
                  tabs={tabs.length}
                  active={selectedIndex}
                  tabsize={componentSize || "medium"}
                  variant={variant}
                >
                  {// If valid element, assume that the callee will provide such element that UI works, otherwise (most likely value is just a string) wrap with label
                  isValidElement(tab.value) ? (
                    tab.value
                  ) : (
                    <TabLabel>{tab.value}</TabLabel>
                  )}
                </Tab>
              )
            })}
          </TabList>
        </TabListContainer>
      </TabListWrapper>
      {tabPanels.map((panel, index: number) => {
        const selected = selectedIndex === index
        return (
          <TabPanel
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            variant={variant}
            visible={selected || forceRenderTabPanel}
          >
            {panel.content}
          </TabPanel>
        )
      })}
    </ReactTabs>
  )
}

TabList.defaultProps = {
  alignment: "center",
}

Tabs.defaultProps = {
  componentSize: "medium",
  variant: "pills",
}

Tab.defaultProps = {
  tabsize: "medium",
}

Tabs.testClassName = "tabs"
